import React, { useEffect, useMemo, useState } from 'react';
import { Shell } from '../../components/layout/Shell';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Sparkle, Plus, FileText, ListChecks, X, CheckCircle, Package, Lightning, CircleNotch, CaretDown, CaretRight, Eye } from '@phosphor-icons/react';

const ARCHETYPE_META = {
  'trust-heavy': { label: 'TRUST', className: 'bg-blue-100 text-blue-900' },
  'judgment-heavy': { label: 'JUDGMENT', className: 'bg-purple-100 text-purple-900' },
  'performance-heavy': { label: 'PERFORMANCE', className: 'bg-green-100 text-green-900' },
  'precision-heavy': { label: 'PRECISION', className: 'bg-amber-100 text-amber-900' },
  'reliability-heavy': { label: 'RELIABILITY', className: 'bg-slate-200 text-slate-900' },
  'artifact-heavy': { label: 'ARTIFACT', className: 'bg-pink-100 text-pink-900' },
};

/**
 * Admin Role Family Auto-Generator + Spec Seeder
 */
const RoleFamiliesAdmin = () => {
  const [families, setFamilies] = useState([]);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [generating, setGenerating] = useState({}); // { [rfId]: true }
  const [sectorFilter, setSectorFilter] = useState('all');
  const [expandedFamily, setExpandedFamily] = useState(null);
  const [familyAssessments, setFamilyAssessments] = useState({}); // { [rfId]: [tests] }
  const [expandedAssessment, setExpandedAssessment] = useState(null);

  const loadList = async () => {
    const { data } = await api.get('/role-families');
    setFamilies(data || []);
  };

  useEffect(() => { loadList(); }, []);
  // Poll while any family has in-flight generation
  useEffect(() => {
    if (!Object.keys(generating).length) return;
    const t = setInterval(loadList, 6000);
    return () => clearInterval(t);
  }, [generating]);

  const seedSpec = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post('/role-families/seed-spec');
      toast.success(`Seeded: ${data.created} created, ${data.patched} patched, ${data.existing} untouched (total ${data.total})`);
      await loadList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Seed failed.');
    } finally {
      setSeeding(false);
    }
  };

  const generateFor = async (rfId, rfName) => {
    setGenerating((s) => ({ ...s, [rfId]: true }));
    try {
      const { data } = await api.post(`/role-families/${rfId}/generate-assessments`);
      toast.success(`${rfName}: ${data.queued.length} tests queued (${data.skipped.length} already exist)`);
      setTimeout(() => setGenerating((s) => { const n = { ...s }; delete n[rfId]; return n; }), Math.max(30000, data.queued.length * 30000));
    } catch (e) {
      setGenerating((s) => { const n = { ...s }; delete n[rfId]; return n; });
      toast.error(e?.response?.data?.detail || 'Generation failed to queue.');
    }
  };

  const toggleFamily = async (rfId) => {
    if (expandedFamily === rfId) {
      setExpandedFamily(null);
      setExpandedAssessment(null);
      return;
    }
    setExpandedFamily(rfId);
    setExpandedAssessment(null);
    if (!familyAssessments[rfId]) {
      try {
        const { data } = await api.get(`/assessments?role_family_id=${rfId}`);
        setFamilyAssessments((m) => ({ ...m, [rfId]: data || [] }));
      } catch (e) {
        toast.error('Could not load assessments');
      }
    }
  };

  const sectors = useMemo(() => {
    const s = new Set();
    families.forEach((f) => { if (f.sector) s.add(f.sector); });
    return ['all', ...Array.from(s).sort()];
  }, [families]);

  const visible = useMemo(() => {
    if (sectorFilter === 'all') return families;
    return families.filter((f) => f.sector === sectorFilter);
  }, [families, sectorFilter]);

  const totalTests = families.reduce((acc, f) => acc + (f.assessment_count || 0), 0);

  return (
    <Shell>
      <div className="max-w-[1280px] mx-auto px-6 py-10">
        <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
          <div>
            <div className="label-eyebrow text-primary mb-2">ADMIN · ROLE FAMILIES</div>
            <h1 className="font-display font-black text-4xl tracking-tighter">Taxonomy & assessment scaffolds.</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              {families.length} families · {totalTests} assessments · archetype-tuned scoring weights. Role families are the normalization layer between jobs and candidates.
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" onClick={seedSpec} disabled={seeding} className="rounded-none" data-testid="seed-spec-btn">
              <Package size={16} className="mr-2" /> {seeding ? 'Seeding…' : 'Seed from spec'}
            </Button>
            <Button onClick={() => setWizardOpen(true)} className="rounded-none" data-testid="open-wizard-btn">
              <Sparkle size={16} weight="fill" className="mr-2" /> Generate new
            </Button>
          </div>
        </div>

        {/* Sector filter */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="sector-filter">
          {sectors.map((s) => (
            <button
              key={s}
              onClick={() => setSectorFilter(s)}
              className={`label-eyebrow px-3 py-1.5 border ${sectorFilter === s ? 'bg-foreground text-background border-foreground' : 'bg-white border-border text-muted-foreground hover:border-foreground'}`}
              data-testid={`sector-chip-${s.replace(/\s+/g,'-').toLowerCase()}`}
            >
              {s === 'all' ? `All · ${families.length}` : `${s} · ${families.filter((f) => f.sector === s).length}`}
            </button>
          ))}
        </div>

        <div className="border border-border" data-testid="role-family-list">
          {visible.length === 0 && (
            <div className="p-8 text-center text-sm text-muted-foreground">No role families match this filter.</div>
          )}
          {visible.map((f) => {
            const arch = ARCHETYPE_META[f.archetype] || { label: 'UNSET', className: 'bg-muted text-muted-foreground' };
            const recTypes = f.recommended_assessment_types || [];
            const genInFlight = !!generating[f.id];
            const needsTests = recTypes.length > 0 && (f.assessment_count || 0) < recTypes.length;
            const isOpen = expandedFamily === f.id;
            const tests = familyAssessments[f.id] || [];
            return (
              <div key={f.id} className="border-b border-border bg-white" data-testid={`rf-row-${f.slug}`}>
                <div className="p-5 flex items-start justify-between gap-4">
                  <button
                    className="min-w-0 flex-1 flex items-start gap-3 text-left hover:opacity-80"
                    onClick={() => toggleFamily(f.id)}
                    data-testid={`rf-toggle-${f.slug}`}
                  >
                    <span className="mt-1 shrink-0 text-muted-foreground">
                      {isOpen ? <CaretDown size={14} /> : <CaretRight size={14} />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`label-eyebrow px-2 py-0.5 ${arch.className}`} title={f.archetype}>{arch.label}</span>
                        {f.sector && <span className="label-eyebrow text-muted-foreground">{f.sector}</span>}
                      </div>
                      <div className="font-display font-black text-lg">{f.name}</div>
                      <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{f.description || f.notes}</div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {(f.required_skills || []).slice(0, 6).map((s) => (
                          <span key={s} className="label-eyebrow bg-secondary px-2 py-0.5">{s}</span>
                        ))}
                      </div>
                      {recTypes.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {recTypes.map((t) => (
                            <span key={t} className="label-eyebrow bg-primary/10 text-primary px-2 py-0.5 mono">{t}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </button>
                  <div className="shrink-0 flex flex-col items-end gap-2">
                    <div className="text-right">
                      <div className="mono text-xs text-muted-foreground">{f.assessment_count || 0} / {recTypes.length || '?'} tests</div>
                      <div className="mono text-[10px] text-muted-foreground mt-0.5">
                        P{f.scoring_weights?.performance} · C{f.scoring_weights?.consistency} · R{f.scoring_weights?.reliability} · A{f.scoring_weights?.artifacts}
                      </div>
                    </div>
                    {needsTests && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-none h-8"
                        onClick={(e) => { e.stopPropagation(); generateFor(f.id, f.name); }}
                        disabled={genInFlight}
                        data-testid={`generate-tests-${f.slug}`}
                      >
                        {genInFlight ? (<><CircleNotch size={12} className="mr-1 animate-spin" />Generating</>) : (<><Lightning size={12} className="mr-1" />Generate tests</>)}
                      </Button>
                    )}
                  </div>
                </div>

                {isOpen && (
                  <div className="bg-secondary/40 border-t border-border px-5 py-4" data-testid={`rf-detail-${f.slug}`}>
                    {tests.length === 0 ? (
                      <div className="text-sm text-muted-foreground py-2">No assessments yet for this family. Click "Generate tests" to create the recommended battery.</div>
                    ) : (
                      <div className="space-y-2">
                        {tests.map((t) => (
                          <AssessmentRow
                            key={t.id}
                            test={t}
                            isOpen={expandedAssessment === t.id}
                            onToggle={() => setExpandedAssessment(expandedAssessment === t.id ? null : t.id)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {wizardOpen && (
        <GenerateWizard
          onClose={() => setWizardOpen(false)}
          onSaved={async () => {
            setWizardOpen(false);
            await loadList();
          }}
        />
      )}
    </Shell>
  );
};

// ─── Assessment row + item viewer ─────────────────────────────────────────

const AssessmentRow = ({ test, isOpen, onToggle }) => {
  return (
    <div className="border border-border bg-white" data-testid={`assessment-row-${test.id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left p-3 flex items-center justify-between gap-3 hover:bg-secondary/30"
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {isOpen ? <CaretDown size={12} /> : <CaretRight size={12} />}
          <span className="label-eyebrow bg-primary/10 text-primary px-2 py-0.5 mono shrink-0">{test.type}</span>
          <span className="font-medium text-sm truncate">{test.title}</span>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="mono text-xs text-muted-foreground">{(test.items || []).length} items</span>
          <span className="mono text-xs text-muted-foreground">{Math.round((test.time_limit_sec || 0) / 60)} min</span>
          <Eye size={14} className="text-muted-foreground" />
        </div>
      </button>
      {isOpen && (
        <div className="border-t border-border p-4 bg-secondary/20 space-y-3" data-testid={`assessment-items-${test.id}`}>
          {(test.specialization_tags || []).length > 0 && (
            <div className="flex flex-wrap gap-1">
              {test.specialization_tags.map((tag) => (
                <span key={tag} className="label-eyebrow bg-secondary px-2 py-0.5">{tag}</span>
              ))}
            </div>
          )}
          {(test.items || []).map((item, idx) => (
            <ItemPreview key={item.id || idx} item={item} idx={idx} type={test.type} />
          ))}
        </div>
      )}
    </div>
  );
};

const ItemPreview = ({ item, idx, type }) => (
  <div className="border border-border bg-white p-3 text-sm">
    <div className="flex items-start gap-2 mb-2">
      <span className="mono text-xs text-muted-foreground shrink-0">Q{idx + 1}</span>
      <div className="font-medium">{item.prompt}</div>
    </div>
    {/* MCQ options */}
    {Array.isArray(item.options) && item.options.length > 0 && (
      <ul className="ml-7 space-y-1 mt-2 text-xs">
        {item.options.map((opt, i) => {
          const correct = type === 'mcq' && i === item.correct_index;
          return (
            <li key={i} className={correct ? 'text-primary font-semibold' : 'text-muted-foreground'}>
              {correct ? '✓ ' : '○ '}{opt}
            </li>
          );
        })}
      </ul>
    )}
    {/* Scenario / writing rubric */}
    {item.rubric && (
      <div className="ml-7 mt-2 text-xs text-muted-foreground">
        <span className="label-eyebrow text-primary mr-1">RUBRIC</span>{item.rubric}
      </div>
    )}
    {/* Stages for simulation */}
    {Array.isArray(item.stages) && item.stages.length > 0 && (
      <div className="ml-7 mt-2 space-y-1">
        {item.stages.map((s, i) => (
          <div key={i} className="text-xs border-l-2 border-primary/40 pl-2">
            <div className="font-medium">{s.label || `Stage ${i + 1}`}: {s.question}</div>
            {Array.isArray(s.expected_signals) && (
              <div className="text-muted-foreground">Looking for: {s.expected_signals.join(' · ')}</div>
            )}
          </div>
        ))}
      </div>
    )}
    {/* Work-style trait */}
    {item.trait && (
      <div className="ml-7 mt-1 text-xs text-muted-foreground mono">trait: {Array.isArray(item.trait) ? item.trait.join(', ') : item.trait}{item.is_reverse ? ' (reverse-scored)' : ''}</div>
    )}
    {/* Skills tags */}
    {Array.isArray(item.skills) && item.skills.length > 0 && (
      <div className="ml-7 mt-2 flex flex-wrap gap-1">
        {item.skills.map((s) => (
          <span key={s} className="label-eyebrow bg-secondary/70 text-foreground/70 px-2 py-0.5">{s}</span>
        ))}
      </div>
    )}
    {item.min_words && (
      <div className="ml-7 mt-1 text-[10px] text-muted-foreground mono">min {item.min_words} words</div>
    )}
  </div>
);

// ─── Wizard Modal ─────────────────────────────────────────────────────────

const GenerateWizard = ({ onClose, onSaved }) => {
  // steps: 'input' -> 'generating' -> 'preview' -> 'saving'
  const [step, setStep] = useState('input');
  const [roleName, setRoleName] = useState('');
  const [description, setDescription] = useState('');
  const [context, setContext] = useState('');
  const [preview, setPreview] = useState(null); // { role_family, mcq_assessment, writing_assessment }
  const [error, setError] = useState(null);

  const generate = async () => {
    if (!roleName.trim()) {
      toast.error('Role name is required.');
      return;
    }
    setError(null);
    setStep('generating');
    try {
      const { data } = await api.post('/role-families/generate', {
        role_name: roleName,
        description,
        context,
      });
      setPreview(data);
      setStep('preview');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Generation failed.');
      setStep('input');
    }
  };

  const commit = async () => {
    if (!preview) return;
    setStep('saving');
    try {
      const payload = {
        role_family: preview.role_family,
        assessments: [
          { ...preview.mcq_assessment, type: 'mcq' },
          { ...preview.writing_assessment, type: 'writing' },
        ],
      };
      const { data } = await api.post('/role-families/commit', payload);
      toast.success(`Saved "${data.role_family.name}" + ${data.assessments_created.length} assessments.`);
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Save failed.');
      setStep('preview');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto p-4 sm:p-8" data-testid="wizard-modal">
      <div className="bg-background border border-border max-w-3xl w-full my-6">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-2">
            <Sparkle size={20} weight="fill" className="text-primary" />
            <div className="font-display font-black text-xl">Generate role family</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-secondary" data-testid="wizard-close">
            <X size={18} />
          </button>
        </div>

        {step === 'input' && (
          <div className="p-6 space-y-4">
            <p className="text-sm text-muted-foreground">
              Type any role. Claude 4.5 will scaffold a role family with skills, scoring weights, an MCQ test, and a writing test tailored to the real work.
            </p>
            <div>
              <Label className="label-eyebrow text-muted-foreground">Role name *</Label>
              <Input
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
                placeholder="e.g. Registered Nurse, Paralegal, Field Sales Rep"
                className="mt-2 rounded-none h-11"
                data-testid="wizard-role-name"
              />
            </div>
            <div>
              <Label className="label-eyebrow text-muted-foreground">Description (optional)</Label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="One line summarizing the job"
                className="mt-2 rounded-none h-11"
                data-testid="wizard-desc"
              />
            </div>
            <div>
              <Label className="label-eyebrow text-muted-foreground">Extra context (optional)</Label>
              <Textarea
                value={context}
                onChange={(e) => setContext(e.target.value)}
                placeholder="Industry specifics, seniority level, must-have skills, tools used..."
                className="mt-2 rounded-none min-h-[80px]"
                data-testid="wizard-context"
              />
            </div>
            {error && <div className="text-sm text-destructive border border-destructive/40 bg-destructive/5 p-3" data-testid="wizard-error">{error}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" className="rounded-none" onClick={onClose} data-testid="wizard-cancel">Cancel</Button>
              <Button onClick={generate} className="rounded-none" disabled={!roleName.trim()} data-testid="wizard-generate-btn">
                <Sparkle size={14} weight="fill" className="mr-1" /> Generate
              </Button>
            </div>
          </div>
        )}

        {step === 'generating' && (
          <div className="p-12 text-center" data-testid="wizard-generating">
            <div className="inline-block animate-pulse">
              <Sparkle size={40} weight="fill" className="text-primary" />
            </div>
            <div className="mono text-sm text-muted-foreground mt-4">Claude is thinking...</div>
            <div className="text-xs text-muted-foreground mt-1">This usually takes 10–25 seconds.</div>
          </div>
        )}

        {step === 'preview' && preview && (
          <PreviewPanel
            preview={preview}
            setPreview={setPreview}
            onBack={() => setStep('input')}
            onCommit={commit}
            error={error}
          />
        )}

        {step === 'saving' && (
          <div className="p-12 text-center" data-testid="wizard-saving">
            <div className="mono text-sm text-muted-foreground">Saving...</div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Preview / edit panel ─────────────────────────────────────────────────

const PreviewPanel = ({ preview, setPreview, onBack, onCommit, error }) => {
  const rf = preview.role_family;
  const mcq = preview.mcq_assessment;
  const wr = preview.writing_assessment;

  const updateRF = (field, value) => {
    setPreview({ ...preview, role_family: { ...rf, [field]: value } });
  };
  const updateMcqTitle = (v) => setPreview({ ...preview, mcq_assessment: { ...mcq, title: v } });
  const updateWrTitle = (v) => setPreview({ ...preview, writing_assessment: { ...wr, title: v } });

  return (
    <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto" data-testid="wizard-preview">
      <div className="flex items-center gap-2 text-sm text-primary">
        <CheckCircle size={18} weight="fill" /> Generated. Review and edit before saving.
      </div>

      {/* Role family */}
      <section className="border border-border p-4 bg-white">
        <div className="label-eyebrow text-muted-foreground mb-3">ROLE FAMILY</div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label className="label-eyebrow text-muted-foreground">Name</Label>
            <Input value={rf.name} onChange={(e) => updateRF('name', e.target.value)} className="mt-1 rounded-none h-10" data-testid="preview-rf-name" />
          </div>
          <div>
            <Label className="label-eyebrow text-muted-foreground">Slug</Label>
            <Input value={rf.slug} onChange={(e) => updateRF('slug', e.target.value)} className="mt-1 rounded-none h-10 mono" data-testid="preview-rf-slug" />
          </div>
        </div>
        <div className="mt-3">
          <Label className="label-eyebrow text-muted-foreground">Description</Label>
          <Textarea value={rf.description} onChange={(e) => updateRF('description', e.target.value)} className="mt-1 rounded-none" data-testid="preview-rf-desc" />
        </div>
        <div className="mt-3">
          <Label className="label-eyebrow text-muted-foreground">Required skills (comma separated)</Label>
          <Input
            value={(rf.required_skills || []).join(', ')}
            onChange={(e) => updateRF('required_skills', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
            className="mt-1 rounded-none h-10 mono text-xs"
            data-testid="preview-rf-skills"
          />
        </div>
        <div className="mt-3 text-xs mono text-muted-foreground">
          Scoring weights: perf {rf.scoring_weights?.performance} · cons {rf.scoring_weights?.consistency} · rel {rf.scoring_weights?.reliability} · art {rf.scoring_weights?.artifacts}
        </div>
      </section>

      {/* MCQ assessment */}
      <section className="border border-border p-4 bg-white" data-testid="preview-mcq">
        <div className="flex items-center gap-2 mb-3">
          <ListChecks size={16} className="text-primary" />
          <div className="label-eyebrow text-muted-foreground">MCQ ASSESSMENT · {mcq.items?.length || 0} ITEMS</div>
        </div>
        <Input value={mcq.title} onChange={(e) => updateMcqTitle(e.target.value)} className="rounded-none h-10 mb-3" data-testid="preview-mcq-title" />
        <div className="space-y-3">
          {(mcq.items || []).slice(0, 4).map((it, idx) => (
            <div key={idx} className="border border-border p-3 text-sm">
              <div className="font-medium mb-2">{idx + 1}. {it.prompt}</div>
              <ul className="space-y-1 text-xs">
                {(it.options || []).map((opt, i) => (
                  <li key={i} className={i === it.correct_index ? 'text-primary font-medium' : 'text-muted-foreground'}>
                    {i === it.correct_index ? '✓ ' : '  '}{opt}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {(mcq.items || []).length > 4 && (
            <div className="text-xs text-muted-foreground">+ {mcq.items.length - 4} more items...</div>
          )}
        </div>
      </section>

      {/* Writing assessment */}
      <section className="border border-border p-4 bg-white" data-testid="preview-writing">
        <div className="flex items-center gap-2 mb-3">
          <FileText size={16} className="text-primary" />
          <div className="label-eyebrow text-muted-foreground">WRITING ASSESSMENT · {wr.items?.length || 0} PROMPTS</div>
        </div>
        <Input value={wr.title} onChange={(e) => updateWrTitle(e.target.value)} className="rounded-none h-10 mb-3" data-testid="preview-writing-title" />
        <div className="space-y-3">
          {(wr.items || []).map((it, idx) => (
            <div key={idx} className="border border-border p-3 text-sm">
              <div className="font-medium mb-2">{idx + 1}. {it.prompt}</div>
              <div className="text-xs text-muted-foreground">Rubric: {it.rubric || '—'}</div>
              {it.min_words ? <div className="text-xs text-muted-foreground">Min words: {it.min_words}</div> : null}
            </div>
          ))}
        </div>
      </section>

      {error && <div className="text-sm text-destructive border border-destructive/40 bg-destructive/5 p-3" data-testid="preview-error">{error}</div>}

      <div className="flex justify-between pt-2 sticky bottom-0 bg-background py-3 border-t border-border">
        <Button variant="outline" className="rounded-none" onClick={onBack} data-testid="preview-back">← Back</Button>
        <Button onClick={onCommit} className="rounded-none" data-testid="preview-commit">
          <Plus size={14} className="mr-1" /> Save role family + tests
        </Button>
      </div>
    </div>
  );
};

export default RoleFamiliesAdmin;
